import type { ById } from '../utils'

export enum SourceFileType {
  PDF = 'pdf',
  CDP = 'cdp',
  Factset = 'factset',
  Excel = 'xlsx',
  Website = 'website',
  Other = 'other',
}

type SourcePredictions = {
  modelId: string
  confidencePerLabel: Record<string, number>
}

export type PageConfidence = {
  pageNumber: number | undefined
  confidence: number | undefined
}

export type PredictionSummary = Partial<PageConfidence> & {
  numPredictions: number
  currentPredictionIndex: number
  allPredictions: PageConfidence[]
}

export type PagePredictions = {
  modelId: string
  topPagesPerLabel: Record<string, PageConfidence[]>
}

export type Source = {
  id: number
  name: string
  originalUrl: string | undefined
  url: string
  fileType: SourceFileType
  thumbnailUrl?: string
  start?: Date
  end?: Date
  publishingDate?: Date
  // Actually ReportTypes generated type in @netpurpose/api package.
  reportType?: string
  checksum?: string
  entityId?: number
  predictions?: SourcePredictions | undefined
  numUniqueEntityOccurrences?: number | undefined
  status?: SourceStatus
}

export type SourcesById = ById<Source>

export type SourceWithSearchStatus = Source & { isSearched?: boolean }
export type SourcesWithSearchStatusById = ById<SourceWithSearchStatus>

export enum SourceTaskState {
  Blocked = 'BLOCKED',
  Pending = 'PENDING',
  InProgress = 'IN_PROGRESS',
  Complete = 'COMPLETE',
}

export enum SourceTaskStep {
  Extract = 'EXTRACT',
  Verify = 'VERIFY',
}

export enum SourceStatus {
  // This is a valid enum value but Knip complains because we never use it. When
  // we switch to the generated type it won't be an issue.
  // Candidate = 'CANDIDATE',
  CheckEntity = 'CHECK ENTITY',
  Extracted = 'EXTRACTED',
  New = 'NEW',
  Rejected = 'REJECTED',
  Verified = 'VERIFIED',
}

export enum SourceTaskType {
  NewEntity = 'NEW_ENTITY',
  Backfill = 'BACKFILL',
  Update = 'UPDATE',
}

export type SearchLink = {
  url: string
  entityName: string
  year: number
  reportType: string
  description: string
}

export type SourceBatch = {
  id: string
  phase: number
  step: SourceTaskStep
  state: SourceTaskState
  totalTasks: number
  completedTasks: number
  outstandingTasks: number
  totalTimeSpent: string
  expectedTimeRemaining: string
  entityId: number
  priority: number
}

export type SourceBatchWithTasks = SourceBatch & {
  tasks: SourceTask[]
}

export type SourceTask = {
  id: number
  batchId: SourceBatch['id']
  entityId: number
  entityName?: string
  priority: number
  state: SourceTaskState
  years: number[]
  userId?: string
  username?: string
  step: SourceTaskStep
  expectedTimeRemaining: string
  totalTimeSpent?: string
  searchLinks: SearchLink[]
  message: string
  type: SourceTaskType
  sources?: Source[]
}

export enum SourceUploadMode {
  Url = 'url',
  File = 'file',
}

interface BaseSourceUpload {
  sourceType: SourceFileType
  fileName: string
  // Actually ReportTypes generated type in @netpurpose/api package.
  reportType: string
  startDate: Date | undefined
  endDate: Date
  publishingDate?: Date
  entityId: number | undefined
  sourceUrl: string | undefined
  status: SourceStatus | undefined
}

interface UrlSourceUpload extends BaseSourceUpload {
  mode: SourceUploadMode.Url
}

interface FileSourceUpload extends BaseSourceUpload {
  mode: SourceUploadMode.File
  sourceFile: File
}

export type SourceUpload = UrlSourceUpload | FileSourceUpload

export type SourceWithEntityName = Source & {
  entityName?: string
  searchTerm?: string
}
